<template>
  <div class="so-stock-movements-table">
    <div v-if="isLoading" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      {{ t('Carregando movimentações...') }}
    </div>

    <div v-else-if="movements.length === 0" class="empty-state">
      <i class="pi pi-info-circle"></i>
      {{ t('Nenhuma movimentação encontrada') }}
    </div>

    <table v-else class="movements-table">
      <thead>
        <tr>
          <th>{{ t('Data') }}</th>
          <th>{{ t('Tipo') }}</th>
          <th>{{ t('Quantidade') }}</th>
          <th>{{ t('Referência') }}</th>
          <th>{{ t('Observações') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="movement in movements" :key="movement.id || movement.so_stock_movement_id">
          <td>{{ formatMovementDate(movement.so_stock_movement_date || movement.created_at) }}</td>
          <td>
            <span :class="getTypeClass(movement.so_stock_movement_type)">
              {{ getTypeLabel(movement.so_stock_movement_type) }}
            </span>
          </td>
          <td :class="getQuantityClass(movement.so_stock_movement_quantity)">
            {{ formatQuantity(movement.so_stock_movement_quantity) }}
          </td>
          <td>{{ movement.so_stock_movement_reference || '-' }}</td>
          <td>{{ movement.so_stock_movement_notes || '-' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOStock } from '../../composables/useSOStock';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { log, error as logError } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface Movement {
  id?: Id;
  so_stock_movement_id?: Id;
  so_stock_movement_date?: string;
  created_at?: string;
  so_stock_movement_type?: string;
  so_stock_movement_quantity?: number | string | null;
  so_stock_movement_reference?: string;
  so_stock_movement_notes?: string;
}

interface Props {
  productId: Id;
}

const props = defineProps<Props>();

// Get i18n composable
const { t } = useI18n();

// Use composables
const { fetchMovements } = useSOStock();
const { formatDateTime } = useDateFormatter();

// Reactive data
const movements = ref<Movement[]>([]);
const isLoading = ref<boolean>(false);

// Methods
const loadMovements = async (): Promise<void> => {
  if (!props.productId) {
    log('SOStockMovementsTable', 'loadMovements', 'No productId provided');
    return;
  }

  log('SOStockMovementsTable', 'loadMovements', 'Loading movements for product', props.productId);
  isLoading.value = true;

  try {
    movements.value = await fetchMovements(Number(props.productId)) as Movement[];
    log('SOStockMovementsTable', 'loadMovements', 'Movements loaded', movements.value.length);
  } catch (err: unknown) {
    logError('SOStockMovementsTable', 'loadMovements', 'Error loading movements', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar movimentações') + ': ' + message);
    movements.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatMovementDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return formatDateTime(date);
  } catch {
    return dateStr;
  }
};

const getTypeLabel = (type?: string): string => {
  const labels: Record<string, string> = {
    adjustment: t('Ajuste'),
    order: t('Pedido'),
    return: t('Devolução')
  };
  return (type && labels[type]) || type || '-';
};

const getTypeClass = (type?: string): Record<string, boolean> => {
  return {
    'type-badge': true,
    'type-adjustment': type === 'adjustment',
    'type-order': type === 'order',
    'type-return': type === 'return'
  };
};

const formatQuantity = (qty?: number | string | null): string => {
  if (qty === null || qty === undefined) return '-';
  const num = Number(qty);
  return num > 0 ? `+${num}` : `${num}`;
};

const getQuantityClass = (qty?: number | string | null): Record<string, boolean> => {
  const num = Number(qty);
  return {
    'quantity-positive': num > 0,
    'quantity-negative': num < 0
  };
};

// Watch for productId changes
watch(() => props.productId, (newId) => {
  log('SOStockMovementsTable', 'watch', 'productId changed', { newId });
  if (newId) {
    loadMovements();
  }
});

// Lifecycle
onMounted(() => {
  log('SOStockMovementsTable', 'mounted', 'Component mounted', { productId: props.productId });
  if (props.productId) {
    loadMovements();
  }
});
</script>

<style scoped>
.so-stock-movements-table {
  width: 100%;
}

.loading-state,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--p-text-muted-color);
  font-size: 0.95rem;
}

.movements-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.movements-table th {
  background-color: var(--p-surface-100);
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: var(--p-text-color);
  border-bottom: 2px solid var(--p-surface-200);
}

.movements-table td {
  padding: 0.6rem 1rem;
  border-bottom: 1px solid var(--p-surface-200);
  color: var(--p-text-color);
}

.movements-table tbody tr:hover {
  background-color: var(--p-surface-50);
}

.type-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.type-adjustment {
  background-color: var(--p-blue-50);
  color: var(--p-blue-700);
}

.type-order {
  background-color: var(--p-green-50);
  color: var(--p-green-700);
}

.type-return {
  background-color: var(--p-orange-50);
  color: var(--p-orange-700);
}

.quantity-positive {
  color: var(--p-green-600);
  font-weight: 600;
}

.quantity-negative {
  color: var(--p-red-600);
  font-weight: 600;
}
</style>
